/* eslint-disable no-nested-ternary */
import React, { useContext, Fragment, useState, useEffect } from 'react';
import { NavBar, Tabs, List, Popup, Badge, Button, Divider, ProgressCircle, Toast, Ellipsis, Dialog } from 'antd-mobile';
import { CloseOutline } from 'antd-mobile-icons';
import moment from 'moment';
import get from 'lodash/get';
import styled from 'styled-components';
import { createOrEditWorkEntry, deleteWorkEntry, getWorkFinalizationChecklist, stopWork } from '../api';
import InputComponent from './Input';
import Files from '../containers/Files';
import { SQL_DATE_FORMAT } from '../utils/constants';
import { getActiveWorkDescription, getDateTimeFormat, getFormattedTotalTime } from '../utils/helpers';
import UserSelfContext from '../utils/self-context';
import WorkFinalizationChecklistPopup from './WorkFinalizationChecklistPopup';

const WorkEntryWrapper = styled.div`
	width: 100%;
	height: 100%;
	display: flex;
	flex-flow: column;
`;
const ContentWrapper = styled.div`
	flex: 1;
	min-height: 0;
	overflow: auto;
	width: 100%;
	background: #f0f2f5;
`;

const WorkEntryInfo = styled.div`
	padding: 0px 12px;
	background: #fff;

	.adm-list-item-title {
		margin: 0px 8px 0px 0px;
		display: flex;
		align-items: center;
	}

	.adm-ellipsis {
		a {
			margin-left: 8px;
		}
	}
`;

const ButtonWrapper = styled.div`
	padding: 8px;
	margin-top: 12px;
`;

const Overview = styled.div`
	padding: 12px 0px;
`;

const StartEndTimeSeparator = styled.span`
	margin: 0px 4px;
`;

const Progress = styled(ProgressCircle)`
	font-size: 12px;
	padding-top: 8px;
`;

const WorkDescription = styled.div`
	font-size: var(--adm-font-size-9);
	margin: 4px 0px;
`;


const Nav = styled(NavBar)`
	background: #fff;
	display: flex;
	border-bottom: solid 1px var(--adm-color-border);
`;

const TabBar = styled(Tabs)`
	display: flex;
	flex-direction: column;
	height: 100%;
	.adm-tabs-header {
		background: #fff;
	}

	.adm-tabs-content {
		flex: 1;
		overflow: auto;
		padding: 0;
	}
`;

const ToastContent = styled.div`
	word-break: normal;
	text-align: center;
	width: 100%;
`;

const Save = styled(Button)`
	font-size: 17px;
`;

function WorkEntryPopup(props) {
	const {
		workEntry,
		visible,
		onClose,
		onFilesChange,
		onSubmit
	} = props;
	const { userSelf, fetchSelf } = useContext(UserSelfContext);
	const [showChecklistPopup, setShowChecklistPopup] = useState(false);
	const [selectedChecklist, setSelectedChecklist] = useState({});
	const [workFinalizationChecklists, setWorkFinalizationChecklists] = useState([]);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [fileIds, setFileIds] = useState([]);

	const [values, setValues] = useState({
		...workEntry
	});

	function handleFilesChange(ids) {
		setFileIds(ids);
		onFilesChange(ids);
	}

	async function updateWorkEntry() {
		const fieldsValues = values;

		try {
			setSaving(true);
			if (workEntry.id && workEntry.id === userSelf.active_work_entry_id) {
				await stopWork(fieldsValues);

				setSaving(false);
				Toast.show({
					icon: 'success',
					content: 'Work stopped'
				});

				onSubmit(fieldsValues);

				await fetchSelf(true);
				return;
			}

			fieldsValues.parent_id = workEntry.parent_id || null;

			if (workEntry.id) {
				fieldsValues.id = workEntry.id;
			}

			if (!workEntry.id) {
				fieldsValues.user_id = userSelf.id;
				fieldsValues.file_ids = fileIds;
			}

			fieldsValues.parent_type = workEntry.parent_type || 'other';

			await createOrEditWorkEntry(userSelf.id, {
				...fieldsValues
			});

			onSubmit(fieldsValues);
			setSaving(false);
		} catch (e) {
			setSaving(false);
			Toast.show({
				icon: 'fail',
				content: <ToastContent>
					Unable to save work entry. Something went wrong.
				</ToastContent>
			});
		}
	}

	async function handleDelete(id) {
		const result = await Dialog.confirm({
			cancelText: 'Cancel',
			confirmText: 'Yes',
			style: { textAlign: 'center' },
			content: 'Delete this Work Entry?'
		});

		if (result) {
			setSaving(true);
			try {
				await deleteWorkEntry(userSelf.id, id);
				await fetchSelf(true);
				onSubmit();
			} catch (e) {
				Toast.show({
					icon: 'fail',
					content: <ToastContent>
						Unable to delete the Work Entry. Something went wrong.
					</ToastContent>
				});
			}

			setSaving(false);
		}
	}

	async function fetchChecklists(type = 'work_order') {
		setLoading(true);

		try {
			const data = await getWorkFinalizationChecklist(`?work_type=${type}`);
			setWorkFinalizationChecklists(data);
		} catch (err) {
			return;
		}

		setLoading(false);
	}

	const getWorkDuration = () => {
		const start = values.work_start ? moment(values.work_start) : moment();
		const end = values.work_end ? moment(values.work_end) : moment();

		if (!start || !end) {
			return 0;
		}

		start.startOf('second');
		end.startOf('second');

		const difference = end.diff(start);

		return moment.duration(difference).asMinutes();
	};

	useEffect(() => {
		if (workEntry && visible) {
			setValues({
				...workEntry
			});
			fetchChecklists(workEntry.other_type_value || 'work_order');
		}
	}, [visible, workEntry]);

	async function onValueChange(data) {
		setValues({ ...values, ...data });
	}

	const workDurationValue = getWorkDuration();

	return (
		<Popup
			position='right'
			visible={visible}
			bodyStyle={{ width: '100%' }}
			onClose={onClose}
		>
			<WorkEntryWrapper>
				<Nav
					backArrow={<CloseOutline />}
					onBack={onClose}
					back={''}
					right={<Save fill='none' loading={saving || loading} onClick={updateWorkEntry}>Save</Save>}
				>
					Work Entry
				</Nav>
				<WorkEntryInfo>
					<List.Item
						title={<Fragment>
							<span>{`${values.work_start ? moment(values.work_start).format(getDateTimeFormat(userSelf)) : ''}`}</span>
							<StartEndTimeSeparator> - </StartEndTimeSeparator>
							<span>{`${values.work_end ? moment(values.work_end).format(get(userSelf, 'settings.time_format')) : 'Working...'}`}</span>
						</Fragment>}
						description={<Fragment>
							Total: {workDurationValue ? getFormattedTotalTime(workDurationValue) : '00h 00min'}
							<StartEndTimeSeparator> &middot; </StartEndTimeSeparator>
							Break: {values.break_duration ? getFormattedTotalTime(values.break_duration) : '00h 00min'}
						</Fragment>}
					>
						<WorkDescription>
							<Ellipsis
								direction='end'
								rows={3}
								content={getActiveWorkDescription(workEntry)}
								expandText="Show"
								collapseText="Hide"
							/>
						</WorkDescription>
					</List.Item>
				</WorkEntryInfo>
				<ContentWrapper>
					<TabBar defaultActiveKey="log">
						<Tabs.Tab
							title="Details"
							key='log'
						>
							<Overview>
								<List>
									<InputComponent
										placeholder="What are you working on?"
										onSave={onValueChange}
										dataKey="other_type_value"
										title={'Work description'}
										isRequired={true}
										disabled={!!get(workEntry, 'parent_id')}
										type="select"
										options={[
											...[
												...(get(userSelf, 'settings.work_log_types') || []),
												'Other Work'
											].map(t => ({ label: t, value: t })),
											...(workEntry.parent_id ? getActiveWorkDescription(workEntry) : [])
										]}
										initialValue={workEntry.parent_id ? [getActiveWorkDescription(workEntry)] : values.other_type_value ? [values.other_type_value] : []}
									/>
									<InputComponent
										placeholder="Work started"
										onSave={
											async (val) => {
												const start = moment(`${val.start_date} ${val.start_time}`, `${SQL_DATE_FORMAT} HH:mm:ss`);
												onValueChange({
													work_start: start
												});
											}
										}
										dataKey="start_date"
										secondKey="start_time"
										title={'Work start'}
										isRequired={true}
										type="datetime"
										initialValue={values.work_start ? new Date(values.work_start) : null}
									/>
									<InputComponent
										onSave={
											async (val) => {
												const start = moment(`${val.start_date} ${val.start_time}`, `${SQL_DATE_FORMAT} HH:mm:ss`);
												onValueChange({
													work_end: start
												});
											}
										}
										dataKey="start_date"
										secondKey="start_time"
										isRequired={true}
										title="Work end"
										type="datetime"
										initialValue={values.work_end ? new Date(values.work_end) : null}
									/>
								</List>
								<Divider>Work duration: {getFormattedTotalTime(workDurationValue)}</Divider>
								<List>
									<InputComponent
										onSave={
											async (val) => {
												const breakStart = moment(`${val.start_date} ${val.start_time}`, `${SQL_DATE_FORMAT} HH:mm:ss`);
												onValueChange({
													break_start: breakStart
												});
											}
										}
										dataKey="start_date"
										secondKey="start_time"
										title="Break start"
										max={values.work_end ? new Date(values.work_end) : null}
										min={values.work_start ? new Date(values.work_start) : null}
										type="datetime"
										initialValue={values.break_start ? new Date(values.break_start) : null}
									/>
									<InputComponent
										title="Break duration (min)"
										onSave={onValueChange}
										dataKey="break_duration"
										placeholder="Break duration in minutes"
										type="stepper"
										min={0}
										step={5}
										initialValue={values.break_duration || 0}
									/>
								</List>
								<Divider>Break: {getFormattedTotalTime(values.break_duration)}</Divider>
								<List>
									<InputComponent
										title="Comment"
										type="text"
										placeholder="Add comment"
										onSave={onValueChange}
										dataKey="comment"
										initialValue={values.comment}
									/>
								</List>
								{!!workFinalizationChecklists.length && <List header="Checklists">
									{workFinalizationChecklists.map((checklist) => {
										let progress = <Progress percent={0}>N/A</Progress>;

										const checkedItems = get(values, `work_finalization_data[${checklist.id}].checked`) || [];
										const checkedCount = checkedItems.length;
										const done = checkedCount === checklist.checklist.length;

										const precent = (checkedCount / checklist.checklist.lenght) * 100;
										progress = <Progress
											percent={precent}
											style={{
												'--fill-color': done ? 'var(--adm-color-success)' : 'var(--adm-color-primary)'
											}}
										>
											{ done ? 'OK' : `${checkedCount}/${checklist.checklist.length}`}
										</Progress>;

										return <List.Item
											extra={progress}
											key={checklist.id}
											onClick={() => {
												setSelectedChecklist(checklist);
												setShowChecklistPopup(true);
											}}
											clickable
										>
											{checklist.title || '-'}
										</List.Item>;
									})}
								</List>}
								<WorkFinalizationChecklistPopup
									visible={showChecklistPopup}
									checklistData={values.work_finalization_data || {}}
									disabled={workEntry.id ? workEntry.user_id !== userSelf.id : false}
									checklist={selectedChecklist}
									saving={saving}
									onSave={(data) => {
										onValueChange(data);
										setShowChecklistPopup(false);
									}}
									onClose={() => setShowChecklistPopup(false)}
								/>
								{workEntry.id && <ButtonWrapper>
									<Button
										block
										saving={saving}
										onClick={() => handleDelete(workEntry.id)}
										color="danger"
										fill="outline"
									>
										Delete
									</Button>
								</ButtonWrapper>}
							</Overview>
						</Tabs.Tab>
						<Tabs.Tab
							title={<Badge
								content={workEntry.files_count || null}
								style={{ '--color': 'var(--adm-color-primary)', '--right': '-10px', '--top': '4px' }}>
									Files
							</Badge>}
							key='files'
						>
							<Files
								objectType="work_entry"
								inPopup={true}
								onChange={handleFilesChange}
								objectId={workEntry.id}
							/>
						</Tabs.Tab>
					</TabBar>
				</ContentWrapper>
			</WorkEntryWrapper>
		</Popup>
	);
}

export default WorkEntryPopup;
